/**
 * Leviathan Web - Customer Creation Wizard
 * Quasar Prime LLC
 * Step-by-step guided form for creating new customers
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '@/utils/api';
import {
  readApiFailure,
  focusFirstFieldError,
  humanizeFieldPath,
  type FieldErrors,
} from '@/utils/formErrors';
import { Wizard, WizardStep } from '@/components/ui/Wizard';
import { FieldError } from '@/components/ui/FieldError';
import {
  ArrowLeft,
  Building2,
  User,
  MapPin,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

// Fallback constants if API fails
const DEFAULT_CUSTOMER_TYPES = [
  { code: 'COMMERCIAL', label: 'Commercial', description: 'Business customers' },
  { code: 'RESIDENTIAL', label: 'Residential', description: 'Home customers' },
  { code: 'GOVERNMENT', label: 'Government', description: 'Government agencies' },
  { code: 'INDUSTRIAL', label: 'Industrial', description: 'Industrial facilities' },
];

const PAYMENT_TERMS = [
  { value: 'NET_15', label: 'Net 15', description: 'Due in 15 days' },
  { value: 'NET_30', label: 'Net 30', description: 'Due in 30 days' },
  { value: 'NET_45', label: 'Net 45', description: 'Due in 45 days' },
  { value: 'NET_60', label: 'Net 60', description: 'Due in 60 days' },
  { value: 'COD', label: 'COD', description: 'Cash on Delivery' },
  { value: 'PREPAID', label: 'Prepaid', description: 'Payment before delivery' },
];

const DEFAULT_PRICING_TIERS = [
  { code: 'STANDARD', name: 'Standard', description: 'Base pricing' },
  { code: 'PREFERRED', name: 'Preferred', description: 'Volume discount' },
  { code: 'PREMIUM', name: 'Premium', description: 'Best rates' },
  { code: 'CUSTOM', name: 'Custom', description: 'Contract pricing' },
];

const DEFAULT_INDUSTRIES = [
  { name: 'Construction' },
  { name: 'Manufacturing' },
  { name: 'Transportation' },
  { name: 'Agriculture' },
  { name: 'Retail' },
  { name: 'Healthcare' },
  { name: 'Education' },
  { name: 'Hospitality' },
  { name: 'Other' },
];

interface PriceBook {
  id: string;
  name: string;
}

interface CustomerTypeConfig {
  id: string;
  code: string;
  label: string;
  description?: string;
}

interface IndustryConfig {
  id: string;
  name: string;
  description?: string;
}

interface PricingTierConfig {
  id: string;
  code: string;
  name: string;
  description?: string;
}

/**
 * A monotonic counter, so failing twice on the same field still navigates the
 * second time. A bare step id would compare equal and the wizard would sit
 * still while the operator wondered why nothing happened.
 */
let jumpNonceCounter = 0;
const nextJumpNonce = () => (jumpNonceCounter += 1);

/**
 * The payload renames fields on the way out, so the API's issue paths have to
 * be renamed on the way back before they can be attached to inputs. Without
 * this an error on `billingZip` would look for a form field of that name,
 * find nothing, and silently show no marker at all — a worse failure than the
 * banner, because it looks like the field is fine.
 */
const API_PATH_TO_FIELD: Record<string, string> = {
  billingAddress: 'address',
  billingCity: 'city',
  billingState: 'state',
  billingZip: 'zip',
  primaryContactFirstName: 'contactFirstName',
  primaryContactLastName: 'contactLastName',
  industryType: 'industry',
  type: 'customerType',
  taxId: 'fein',
  creditTerms: 'paymentTerms',
};

/** Which wizard step holds which field, so the wizard can navigate to it. */
const STEP_FOR_FIELD: Record<string, string> = {
  name: 'basic',
  displayName: 'basic',
  customerNumber: 'basic',
  customerType: 'basic',
  industry: 'basic',
  contactFirstName: 'contact',
  contactLastName: 'contact',
  email: 'contact',
  phone: 'contact',
  fax: 'contact',
  website: 'contact',
  address: 'address',
  city: 'address',
  state: 'address',
  zip: 'address',
  taxExemptNumber: 'business',
  fein: 'business',
  creditLimit: 'credit',
  paymentTerms: 'credit',
  pricingTier: 'credit',
  priceBookId: 'credit',
  notes: 'notes',
};

interface FormData {
  // Basic Info
  name: string;
  displayName: string;
  customerNumber: string;
  customerType: string;
  industry: string;
  // Contact
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  fax: string;
  website: string;
  // Billing Address
  address: string;
  city: string;
  state: string;
  zip: string;
  // Business Details
  taxExempt: boolean;
  taxExemptNumber: string;
  fein: string;
  // Credit & Terms
  creditLimit: string;
  paymentTerms: string;
  pricingTier: string;
  priceBookId: string;
  // Notes
  notes: string;
}

export default function CustomerWizard() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    // Basic Info
    name: '',
    displayName: '',
    customerNumber: '',
    customerType: '',
    industry: '',
    // Contact
    contactFirstName: '',
    contactLastName: '',
    email: '',
    phone: '',
    fax: '',
    website: '',
    // Billing Address
    address: '',
    city: '',
    state: '',
    zip: '',
    // Business Details
    taxExempt: false,
    taxExemptNumber: '',
    fein: '',
    // Credit & Terms
    creditLimit: '',
    paymentTerms: 'NET_30',
    pricingTier: 'STANDARD',
    priceBookId: '',
    // Notes
    notes: '',
  });

  const [priceBooks, setPriceBooks] = useState<PriceBook[]>([]);
  const [customerTypes, setCustomerTypes] = useState<CustomerTypeConfig[]>([]);
  const [industries, setIndustries] = useState<IndustryConfig[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTierConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * "Checked", not "validated" — see `checkAddressFormat`. Nothing here
   * verifies that the address exists.
   */
  const [addressChecked, setAddressChecked] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [jumpToStep, setJumpToStep] = useState<{ stepId: string; nonce: number } | null>(null);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setIsLoading(true);
    try {
      // Fetch all configuration in parallel
      const [priceBookRes, typesRes, industriesRes, tiersRes] = await Promise.all([
        apiFetch(`${API_URL}/api/pricebooks?isActive=true`),
        apiFetch(`${API_URL}/api/configuration/customer-types?isActive=true`),
        apiFetch(`${API_URL}/api/configuration/industries?isActive=true`),
        apiFetch(`${API_URL}/api/configuration/pricing-tiers?isActive=true`),
      ]);

      if (priceBookRes.ok) {
        const data = await priceBookRes.json();
        setPriceBooks(data.priceBooks || []);
      }

      if (typesRes.ok) {
        const data = await typesRes.json();
        setCustomerTypes(data.types?.length > 0 ? data.types : DEFAULT_CUSTOMER_TYPES as any);
      } else {
        setCustomerTypes(DEFAULT_CUSTOMER_TYPES as any);
      }

      if (industriesRes.ok) {
        const data = await industriesRes.json();
        setIndustries(data.industries?.length > 0 ? data.industries : DEFAULT_INDUSTRIES as any);
      } else {
        setIndustries(DEFAULT_INDUSTRIES as any);
      }

      if (tiersRes.ok) {
        const data = await tiersRes.json();
        setPricingTiers(data.tiers?.length > 0 ? data.tiers : DEFAULT_PRICING_TIERS as any);
      } else {
        setPricingTiers(DEFAULT_PRICING_TIERS as any);
      }
    } catch (err) {
      console.error('Failed to fetch configuration:', err);
      // Use defaults on error
      setCustomerTypes(DEFAULT_CUSTOMER_TYPES as any);
      setIndustries(DEFAULT_INDUSTRIES as any);
      setPricingTiers(DEFAULT_PRICING_TIERS as any);
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Any edit to the address invalidates the format check.
    if (['address', 'city', 'state', 'zip'].includes(field as string)) {
      setAddressChecked(false);
    }
    setFieldErrors((prev) => {
      if (prev[field as string] === undefined) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  /**
   * A FORMAT check. It is not address verification, and it is no longer
   * labelled as if it were.
   *
   * What this button did before: checked the four fields were non-empty,
   * checked the state was two characters and the ZIP matched a five-or-nine
   * digit shape, then `await`ed an artificial 500ms delay whose only purpose
   * was to imitate a network round trip, then reported "Address validated"
   * with a green indicator. It contacted nothing. "123 Fake St, Houston, TX
   * 77001" passed, which is exactly what the operator evaluating the demo
   * instance found and asked about.
   *
   * That is the same defect as a health check that hard-codes Redis as
   * connected: a control that reports a verification it never performed. A
   * distributor delivers to physical sites, and an address that has been
   * "validated" is an address nobody double-checks — so the wrong one puts a
   * truck at the wrong site with the badge saying it was fine.
   *
   * The checks themselves are worth keeping: a one-letter state or a
   * four-digit ZIP is a typo, and catching it here is cheaper than catching it
   * on a delivery ticket. So the check stays and the CLAIM changes. Real
   * verification against a postal database (USPS, or a commercial provider)
   * would be worth having and is deliberately not stubbed in here.
   */
  const checkAddressFormat = () => {
    setError(null);
    setAddressChecked(false);

    const { address, city, state, zip } = formData;

    if (!address || !city || !state || !zip) {
      setError('Fill in street, city, state and ZIP before checking the format');
      return;
    }

    if (state.length !== 2) {
      setError('State must be a 2-letter abbreviation');
      return;
    }

    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      setError('ZIP code must be in format 12345 or 12345-6789');
      return;
    }

    setAddressChecked(true);
  };

  const handleComplete = async () => {
    setError(null);
    setIsSaving(true);

    try {
      // Map form fields to Prisma schema field names
      const payload = {
        name: formData.name,
        displayName: formData.displayName || undefined,
        customerNumber: formData.customerNumber || undefined,
        type: formData.customerType as 'COMMERCIAL' | 'RESIDENTIAL' | 'GOVERNMENT' | 'INDUSTRIAL',
        industryType: formData.industry || undefined,
        primaryContactFirstName: formData.contactFirstName || undefined,
        primaryContactLastName: formData.contactLastName || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        fax: formData.fax || undefined,
        website: formData.website || undefined,
        billingAddress: formData.address,
        billingCity: formData.city,
        billingState: formData.state,
        billingZip: formData.zip,
        taxId: formData.taxExempt ? formData.taxExemptNumber : (formData.fein || undefined),
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : undefined,
        creditTerms: formData.paymentTerms ? parseInt(formData.paymentTerms.replace(/\D/g, '') || '30') : 30,
        pricingTier: formData.pricingTier,
        priceBookId: formData.priceBookId || undefined,
        notes: formData.notes || undefined,
        status: 'ACTIVE' as const,
      };

      const response = await apiFetch(`${API_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        /**
         * Field-level, not a banner. The API returns details.issues[] with a
         * path per field; showing only the top-level message means an
         * operator on a seven-step wizard is told "Validation failed" and has
         * to hunt.
         *
         * The payload above RENAMES fields on the way out (`address` becomes
         * `billingAddress`, `contactFirstName` becomes
         * `primaryContactFirstName`), so the API's paths have to be mapped
         * back to the form's own names before they can be attached to inputs
         * — see API_PATH_TO_FIELD.
         */
        const failure = await readApiFailure(response, 'Failed to create customer');
        const mapped: FieldErrors = {};
        for (const [apiPath, message] of Object.entries(failure.fieldErrors)) {
          mapped[API_PATH_TO_FIELD[apiPath] ?? apiPath] = message;
        }

        const paths = Object.keys(mapped);
        setFieldErrors(mapped);
        setError(
          paths.length > 0
            ? paths.map((path) => humanizeFieldPath(path) + ': ' + mapped[path]).join(' · ')
            : failure.message
        );

        const firstStep = paths.map((path) => STEP_FOR_FIELD[path]).find(Boolean);
        if (firstStep) setJumpToStep({ stepId: firstStep, nonce: nextJumpNonce() });
        focusFirstFieldError(mapped);

        setIsSaving(false);
        return;
      }

      const data = await response.json();
      navigate(`/customers/${data.customer.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  // Validation
  const isStep1Valid = formData.name.trim().length > 0 && formData.customerType.length > 0;
  const isStep2Valid = formData.phone.trim().length > 0;
  const isStep3Valid =
    formData.address.trim().length > 0 &&
    formData.city.trim().length > 0 &&
    formData.state.trim().length > 0 &&
    formData.zip.trim().length > 0;
  const isStep4Valid = !formData.taxExempt || formData.taxExemptNumber.trim().length > 0;
  const isStep5Valid = formData.paymentTerms.length > 0;

  // Wizard steps
  const wizardSteps: WizardStep[] = useMemo(
    () => [
      {
        id: 'basic',
        title: 'Basic Info',
        description: 'Company details',
        isValid: isStep1Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Start by entering the company name and type. This information will appear on invoices
              and contracts.
            </p>

            {/* Company Name */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Company Name <span className="text-error-red">*</span>
              </label>
              <input
                data-field="name"
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={'form-input text-lg ' + (fieldErrors.name ? 'border-error-red' : '')}
                placeholder="e.g., ABC Construction Inc."
                autoFocus
              />
              <FieldError error={fieldErrors.name} />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                Legal company name for invoices and contracts
              </p>
            </div>

            {/* Display Name & Customer Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Display Name <span className="text-text-tertiary">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  className="form-input"
                  placeholder="e.g., ABC Construction"
                />
                <p className="text-text-tertiary text-xs font-mono mt-2">Shorter name for internal use</p>
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Customer Number <span className="text-text-tertiary">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.customerNumber}
                  onChange={(e) => updateField('customerNumber', e.target.value.toUpperCase())}
                  className="form-input"
                  placeholder="Auto-generated if blank"
                />
                <p className="text-text-tertiary text-xs font-mono mt-2">Leave blank to auto-generate</p>
              </div>
            </div>

            {/* Customer Type */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer Type <span className="text-error-red">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {customerTypes.map((type) => (
                  <button
                    key={type.code}
                    type="button"
                    onClick={() => updateField('customerType', type.code)}
                    className={`p-4 border text-left transition-all ${
                      formData.customerType === type.code
                        ? 'border-amber bg-amber/10 text-text-primary'
                        : 'border-panel-border bg-background hover:border-amber/50 text-text-secondary'
                    }`}
                  >
                    <div className="font-mono font-semibold text-sm">{type.label}</div>
                    {type.description && (
                      <div className="text-xs text-text-tertiary mt-1">{type.description}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Industry */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Industry <span className="text-text-tertiary">(optional)</span>
              </label>
              <select
                value={formData.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                className="form-input"
              >
                <option value="">Select Industry</option>
                {industries.map((industry) => (
                  <option key={industry.name} value={industry.name}>
                    {industry.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ),
      },
      {
        id: 'contact',
        title: 'Contact',
        description: 'Contact info',
        isValid: isStep2Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Enter the primary contact information for this customer.
            </p>

            {/* Contact Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <User size={24} className="text-amber" />
              <div>
                <div className="font-mono text-text-primary font-semibold">Primary Contact</div>
                <div className="text-text-tertiary text-xs">Who should we contact for orders?</div>
              </div>
            </div>

            {/* Contact Name */}
            {/* Two fields — reports sort by surname, which one field cannot
                support. Last name is the required half when a name is given. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Contact First Name
                </label>
                <input
                  type="text"
                  value={formData.contactFirstName}
                  onChange={(e) => updateField('contactFirstName', e.target.value)}
                  className="form-input"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Contact Last Name
                </label>
                <input
                  type="text"
                  value={formData.contactLastName}
                  onChange={(e) => updateField('contactLastName', e.target.value)}
                  className="form-input"
                  placeholder="Smith"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Email
                </label>
                <input
                  data-field="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={'form-input ' + (fieldErrors.email ? 'border-error-red' : '')}
                  placeholder="orders@company.com"
                />
                <FieldError error={fieldErrors.email} />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Phone <span className="text-error-red">*</span>
                </label>
                <input
                  data-field="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className={'form-input ' + (fieldErrors.phone ? 'border-error-red' : '')}
                  placeholder="(555) 123-4567"
                />
                <FieldError error={fieldErrors.phone} />
              </div>
            </div>

            {/* Fax & Website */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Fax
                </label>
                <input
                  type="tel"
                  value={formData.fax}
                  onChange={(e) => updateField('fax', e.target.value)}
                  className="form-input"
                  placeholder="(555) 123-4568"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  className="form-input"
                  placeholder="https://company.com"
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'address',
        title: 'Address',
        description: 'Billing address',
        isValid: isStep3Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Enter the billing address. Delivery locations (ship-to addresses) can be added after
              creating the customer.
            </p>

            {/* Address Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <MapPin size={24} className="text-steel" />
              <div>
                <div className="font-mono text-text-primary font-semibold">Billing Address</div>
                <div className="text-text-tertiary text-xs">
                  Where should invoices be sent?
                </div>
              </div>
            </div>

            {/* Street Address */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Street Address <span className="text-error-red">*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="form-input"
                placeholder="123 Main Street"
              />
            </div>

            {/* City, State, ZIP */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  City <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="form-input"
                  placeholder="Houston"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  State <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value.toUpperCase().slice(0, 2))}
                  className="form-input"
                  maxLength={2}
                  placeholder="TX"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  ZIP Code <span className="text-error-red">*</span>
                </label>
                <input
                  data-field="zip"
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  className={'form-input ' + (fieldErrors.zip ? 'border-error-red' : '')}
                  placeholder="77001"
                />
                <FieldError error={fieldErrors.zip} />
              </div>
            </div>

            {/* Address FORMAT check.
                Not address verification — nothing here contacts a postal
                database. See `checkAddressFormat` for why the label and the
                success text say so explicitly. */}
            <div className="space-y-2">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={checkAddressFormat}
                  disabled={!formData.address || !formData.city || !formData.state || !formData.zip}
                  className="bg-steel hover:bg-steel/90 text-background font-mono py-2 px-4 text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Check Address Format
                </button>

                {addressChecked && (
                  <div className="flex items-center gap-2 text-success-green font-mono text-sm">
                    <div className="w-4 h-4 rounded-full bg-success-green/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-success-green" />
                    </div>
                    State and ZIP are well-formed
                  </div>
                )}
              </div>

              <p className="text-text-tertiary font-mono text-xs">
                Checks the shape of the state and ZIP only. This instance does not verify that the
                address exists or is deliverable — confirm delivery addresses with the customer.
              </p>
            </div>

            <div className="bg-steel/10 border border-steel/30 p-4">
              <div className="text-steel font-mono text-sm mb-2">Note</div>
              <div className="text-text-secondary font-mono text-sm">
                This is the billing address for invoices. You can add multiple delivery locations
                (ship-to addresses) after creating the customer.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'business',
        title: 'Business',
        description: 'Tax details',
        isValid: isStep4Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Enter tax and business identification details.
            </p>

            {/* Tax Exempt Checkbox */}
            <div className="p-4 bg-background border border-panel-border">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.taxExempt}
                  onChange={(e) => updateField('taxExempt', e.target.checked)}
                  className="form-checkbox bg-background border-panel-border text-amber focus:ring-amber h-5 w-5 mt-0.5"
                />
                <div>
                  <span className="text-text-primary font-mono font-semibold">
                    Tax Exempt Customer
                  </span>
                  <p className="text-text-tertiary text-xs font-mono mt-1">
                    Check if this customer is exempt from sales tax
                  </p>
                </div>
              </label>
            </div>

            {/* Tax Exempt Number */}
            {formData.taxExempt && (
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Tax Exempt Number <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={formData.taxExemptNumber}
                  onChange={(e) => updateField('taxExemptNumber', e.target.value)}
                  className="form-input border-amber"
                  placeholder="75-1234567"
                />
                <p className="text-text-tertiary text-xs font-mono mt-2">
                  Required for tax-exempt customers
                </p>
              </div>
            )}

            {/* Federal EIN */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Federal EIN / FEIN <span className="text-text-tertiary">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.fein}
                onChange={(e) => updateField('fein', e.target.value)}
                className="form-input"
                placeholder="12-3456789"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                Federal Employer Identification Number
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'credit',
        title: 'Credit & Terms',
        description: 'Payment & pricing',
        isValid: isStep5Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Set up payment terms and pricing for this customer.
            </p>

            {/* Credit & Terms Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <CreditCard size={24} className="text-success-green" />
              <div>
                <div className="font-mono text-text-primary font-semibold">Credit & Payment</div>
                <div className="text-text-tertiary text-xs">Set credit limits and payment terms</div>
              </div>
            </div>

            {/* Credit Limit & Payment Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Credit Limit ($) <span className="text-text-tertiary">(optional)</span>
                </label>
                <input
                  data-field="creditLimit"
                  type="number"
                  step="0.01"
                  value={formData.creditLimit}
                  onChange={(e) => updateField('creditLimit', e.target.value)}
                  className={'form-input ' + (fieldErrors.creditLimit ? 'border-error-red' : '')}
                  placeholder="50000.00"
                />
                <FieldError error={fieldErrors.creditLimit} />
                <p className="text-text-tertiary text-xs font-mono mt-2">
                  Leave blank for unlimited credit
                </p>
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Payment Terms <span className="text-error-red">*</span>
                </label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => updateField('paymentTerms', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Terms</option>
                  {PAYMENT_TERMS.map((term) => (
                    <option key={term.value} value={term.value}>
                      {term.label} - {term.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pricing Tier & Price Book */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Pricing Tier
                </label>
                <select
                  value={formData.pricingTier}
                  onChange={(e) => updateField('pricingTier', e.target.value)}
                  className="form-input"
                >
                  {pricingTiers.map((tier) => (
                    <option key={tier.code} value={tier.code}>
                      {tier.name}{tier.description ? ` - ${tier.description}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Price Book <span className="text-text-tertiary">(optional)</span>
                </label>
                <select
                  value={formData.priceBookId}
                  onChange={(e) => updateField('priceBookId', e.target.value)}
                  className="form-input"
                >
                  <option value="">Default Pricing</option>
                  {priceBooks.map((pb) => (
                    <option key={pb.id} value={pb.id}>
                      {pb.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-amber/10 border border-amber/30 p-4">
              <div className="text-amber font-mono text-sm mb-2">Pricing Options</div>
              <div className="text-text-secondary font-mono text-sm space-y-1">
                <div>
                  • <strong>Pricing Tier:</strong> General pricing level
                </div>
                <div>
                  • <strong>Price Book:</strong> Specific product prices
                </div>
                <div>
                  • <strong>Margins:</strong> Can be set per product after creation
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'notes',
        title: 'Notes',
        description: 'Additional info',
        isValid: true,
        isOptional: true,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Add any additional notes or information about this customer.
            </p>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Internal Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                className="form-input min-h-[200px]"
                placeholder="Any additional information about this customer...

Examples:
• Special delivery requirements
• Account history notes
• Contact preferences
• Seasonal ordering patterns"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                These notes are for internal use only and won't appear on invoices
              </p>
            </div>

            <div className="bg-steel/10 border border-steel/30 p-4">
              <div className="text-steel font-mono text-sm mb-2">What's Next?</div>
              <div className="text-text-secondary font-mono text-sm space-y-1">
                <div>After creating the customer, you can:</div>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Add ship-to locations (delivery addresses)</li>
                  <li>Configure product margins</li>
                  <li>Set up delivery programs</li>
                  <li>Create contracts</li>
                  <li>Assign tanks</li>
                </ul>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'review',
        title: 'Review',
        description: 'Confirm details',
        isValid: true,
        content: (
          <div className="space-y-4">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Review the customer information before creating.
            </p>

            {/* Basic Info */}
            <ReviewSection title="Basic Information">
              <ReviewItem label="Company Name" value={formData.name} />
              {formData.displayName && (
                <ReviewItem label="Display Name" value={formData.displayName} />
              )}
              {formData.customerNumber && (
                <ReviewItem label="Customer Number" value={formData.customerNumber} />
              )}
              <ReviewItem
                label="Type"
                value={customerTypes.find((t) => t.code === formData.customerType)?.label || '—'}
              />
              {formData.industry && <ReviewItem label="Industry" value={formData.industry} />}
            </ReviewSection>

            {/* Contact */}
            <ReviewSection title="Contact Information">
              {fullName({ firstName: formData.contactFirstName, lastName: formData.contactLastName }) && (
                <ReviewItem
                  label="Contact"
                  value={
                    fullName({
                      firstName: formData.contactFirstName,
                      lastName: formData.contactLastName,
                    }) ?? ''
                  }
                />
              )}
              {formData.email && <ReviewItem label="Email" value={formData.email} />}
              <ReviewItem label="Phone" value={formData.phone} />
              {formData.fax && <ReviewItem label="Fax" value={formData.fax} />}
              {formData.website && <ReviewItem label="Website" value={formData.website} />}
            </ReviewSection>

            {/* Address */}
            <ReviewSection title="Billing Address">
              <div className="text-text-primary font-mono text-sm">
                {formData.address}
                <br />
                {formData.city}, {formData.state} {formData.zip}
              </div>
            </ReviewSection>

            {/* Business */}
            <ReviewSection title="Business Details">
              <ReviewItem
                label="Tax Status"
                value={
                  formData.taxExempt
                    ? `Tax Exempt (${formData.taxExemptNumber})`
                    : 'Taxable'
                }
              />
              {formData.fein && <ReviewItem label="Federal EIN" value={formData.fein} />}
            </ReviewSection>

            {/* Credit & Pricing */}
            <ReviewSection title="Credit & Pricing">
              {formData.creditLimit && (
                <ReviewItem
                  label="Credit Limit"
                  value={`$${parseFloat(formData.creditLimit).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                  })}`}
                />
              )}
              <ReviewItem
                label="Payment Terms"
                value={
                  PAYMENT_TERMS.find((t) => t.value === formData.paymentTerms)?.label || '—'
                }
              />
              <ReviewItem
                label="Pricing Tier"
                value={
                  pricingTiers.find((t) => t.code === formData.pricingTier)?.name ||
                  'Standard'
                }
              />
              {formData.priceBookId && (
                <ReviewItem
                  label="Price Book"
                  value={priceBooks.find((pb) => pb.id === formData.priceBookId)?.name || '—'}
                />
              )}
            </ReviewSection>

            {/* Notes */}
            {formData.notes && (
              <ReviewSection title="Notes">
                <div className="text-text-primary font-mono text-sm whitespace-pre-wrap">
                  {formData.notes}
                </div>
              </ReviewSection>
            )}
          </div>
        ),
      },
    ],
    [formData, isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid, isStep5Valid, priceBooks, customerTypes, industries, pricingTiers, addressChecked, fieldErrors]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-amber" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link to="/customers" className="back-link mb-3">
              <ArrowLeft size={14} />
              Back to Customers
            </Link>
            <div className="flex items-center gap-3 mt-2">
              <Building2 size={28} className="text-amber" />
              <div>
                <h1
                  className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  New Customer
                </h1>
                <p className="text-text-secondary text-sm font-mono mt-1">
                  Create a new customer account
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      {/* Wizard */}
      <Wizard
        steps={wizardSteps}
        onComplete={handleComplete}
        onCancel={handleCancel}
        title="Create Customer"
        subtitle="Follow the steps below to set up a new customer account"
        isSubmitting={isSaving}
        submitLabel="Create Customer"
        jumpToStep={jumpToStep}
      />
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div className="border-l-2 border-amber pl-4 py-2 mb-3">
        <h4 className="text-amber font-mono text-sm uppercase tracking-wider font-semibold">
          {title}
        </h4>
      </div>
      <div className="bg-panel-dark border border-panel-border p-4 space-y-1">
        {children}
      </div>
    </div>
  );
}

function ReviewItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start py-1.5 border-b border-panel-border last:border-b-0">
      <span className="text-text-tertiary font-mono text-sm min-w-[140px] flex-shrink-0">
        {label}:
      </span>
      <span className="text-text-primary font-mono text-sm ml-4">
        {value || '—'}
      </span>
    </div>
  );
}
